import React, { useEffect, useState, useCallback, useRef } from 'react';
import { OrganizationsManager, Organization } from './components/OrganizationsManager';
import { SubfolderManager } from './components/SubfolderManager';
import { ResponsibleSpecialistsManager, Specialist } from './components/ResponsibleSpecialistsManager';
import { ProgramsManager, Program, ProgramAssignment } from './components/ProgramsManager';
import { StudentsManager, StudentWithPrograms } from './components/students/StudentsManager';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import { Link } from 'react-router-dom';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';

export interface ApplicationFormData {
  organization: Organization | null;
  specialist: Specialist | null;
  subfolderName: string;
  students: StudentWithPrograms[];
}

interface ApplicationFormProps {
  onLogout: () => void;
  initialData?: Partial<ApplicationFormData>;
  isEditing: boolean;
  onSubmit: (data: ApplicationFormData) => Promise<any>;
  onSaveDraft?: (data: ApplicationFormData) => Promise<any>;
  onAutoSaveDraft?: (data: ApplicationFormData) => void;
  username: string;
  role: string;
  initialCommonProgramIds?: Set<string>;
  initialCommonProgramDetails?: Record<string, Partial<any>>;
  initialCommonPrograms?: Program[];
}

export const ApplicationForm: React.FC<ApplicationFormProps> = ({
  onLogout,
  initialData,
  isEditing,
  onSubmit,
  onSaveDraft,
  onAutoSaveDraft,
  username,
  role,
  initialCommonProgramIds,
  initialCommonProgramDetails,
  initialCommonPrograms
}) => {
  const [subfolderName, setSubfolderName] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedSpecialist, setSelectedSpecialist] = useState<Specialist | null>(null);
  const [selectedPrograms, setSelectedPrograms] = useState<Program[]>([]);
  
  const [programAssignments, setProgramAssignments] = useState<ProgramAssignment[] | undefined>(undefined);
  
  const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);
  const [studentsData, setStudentsData] = useState<StudentWithPrograms[]>([]);

  const [studentsVersion, setStudentsVersion] = useState<number>(0);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isStatusVisible, setIsStatusVisible] = useState(false);

  const formDataRef = useRef<ApplicationFormData>({
    organization: null,
    specialist: null,
    subfolderName: '',
    students: []
  });
  const isSubmittedRef = useRef(false);
  const isAutoSavingRef = useRef(false);

  useEffect(() => {
    if (isEditing && initialData) {
      setSubfolderName(initialData.subfolderName || '');
      setSelectedOrg(initialData.organization || null);
      setSelectedSpecialist(initialData.specialist || null);
      setStudentsData(initialData.students || []);
    }
  }, [isEditing, initialData]);

  useEffect(() => {
    formDataRef.current = {
      organization: selectedOrg,
      specialist: selectedSpecialist,
      subfolderName,
      students: studentsData
    };
  }, [selectedOrg, selectedSpecialist, subfolderName, studentsData]);

  const handleAutoSave = useCallback(() => {
    if (isSubmittedRef.current || isAutoSavingRef.current || !onAutoSaveDraft) return;

    const data = formDataRef.current;

    const isEmpty = !data.organization && !data.specialist && !data.subfolderName && data.students.length === 0;
    if (isEmpty) return;

    isAutoSavingRef.current = true;
    onAutoSaveDraft(data);
  }, [onAutoSaveDraft]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      handleAutoSave();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleAutoSave();
    };
  }, [handleAutoSave]);

  useEffect(() => {
    if (selectedOrg?.id !== selectedSpecialist?.organizationId) {
      setSelectedSpecialist(null);
    }
  }, [selectedOrg, selectedSpecialist]);

  useEffect(() => {
    let fadeOutTimer: NodeJS.Timeout;
    let removeTimer: NodeJS.Timeout;

    if (submitSuccess) {
      setIsStatusVisible(true);
      fadeOutTimer = setTimeout(() => setIsStatusVisible(false), 2000);
      removeTimer = setTimeout(() => setSubmitSuccess(null), 2300);
    }

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(removeTimer);
    };
  }, [submitSuccess]);

  const handleOrganizationsLoad = useCallback((orgs: Organization[]) => {
    setOrganizations(orgs);
  }, []);

  const handleOrganizationCreated = useCallback((newOrg: Organization) => {
    // Обработчик создания организации
  }, []);

  const handleStudentsDataChange = useCallback((data: StudentWithPrograms[]) => {
    setStudentsData(prevData => {
      const prevIds = new Set(prevData.map(s => s.id));
      const newStudentAdded = data.some(s => !prevIds.has(s.id));

      let programsChanged = false;
      if (!newStudentAdded && data.length > 0) {
        for (const student of data) {
          const prevStudent = prevData.find(s => s.id === student.id);
          if (prevStudent) {
            const prevProgramIds = prevStudent.programs.map(p => p.program.id);
            const currentProgramIds = student.programs.map(p => p.program.id);

            if (prevProgramIds.length !== currentProgramIds.length) {
              programsChanged = true;
              break;
            }

            const prevProgramIdSet = new Set(prevProgramIds);
            for (let i = 0; i < currentProgramIds.length; i++) {
              if (!prevProgramIdSet.has(currentProgramIds[i])) {
                programsChanged = true;
                break;
              }
            }

            if (programsChanged) break;

            for (let i = 0; i < student.programs.length; i++) {
              const prog = student.programs[i];
              const prevProg = prevStudent.programs.find(p => p.program.id === prog.program.id);
              if (prevProg) {
                if (
                  prevProg.theoretical_start_date !== prog.theoretical_start_date ||
                  prevProg.theoretical_end_date !== prog.theoretical_end_date ||
                  prevProg.exam_date !== prog.exam_date
                ) {
                  programsChanged = true;
                  break;
                }
              }
            }

            if (programsChanged) break;
          }
        }
      }

      if (newStudentAdded || programsChanged) {
        setStudentsVersion(v => v + 1);
      }

      return data;
    });
  }, []);

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitSuccess(null);
    setIsStatusVisible(false);

    if (!selectedOrg) {
      setSubmitError('Необходимо выбрать организацию.');
      setIsStatusVisible(true);
      window.scrollTo(0, 0);
      return;
    }
    if (!selectedSpecialist) {
      setSubmitError('Необходимо выбрать ответственного специалиста.');
      setIsStatusVisible(true);
      window.scrollTo(0, 0);
      return;
    }
    if (studentsData.length === 0) {
      setSubmitError('Необходимо добавить хотя бы одного студента.');
      setIsStatusVisible(true);
      window.scrollTo(0, 0);
      return;
    }

    setIsSubmitting(true);

    const formData: ApplicationFormData = {
      organization: selectedOrg,
      specialist: selectedSpecialist,
      subfolderName: subfolderName,
      students: studentsData
    };

    try {
      const responseData = await onSubmit(formData);
      isSubmittedRef.current = true;

      const successMessage = isEditing
        ? `Успешно сохранено! ${responseData.id ? `ID: ${responseData.id}` : ''}`
        : `Заявка успешно создана! ${responseData.id ? `ID: ${responseData.id}` : ''}`;
      setSubmitError(null);
      setSubmitSuccess(successMessage);
      setIsStatusVisible(true);
    } catch (error: any) {
      console.error('Submission error:', error);
      setSubmitSuccess(null);
      setSubmitError(
        error?.message ||
        `Не удалось ${isEditing ? 'обновить' : 'создать'} заявку. Проверьте консоль.`
      );
      setIsStatusVisible(true);
      window.scrollTo(0, 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraftSave = async () => {
    if (!onSaveDraft) return;

    setSubmitError(null);
    setSubmitSuccess(null);
    setIsStatusVisible(false);
    setIsDraftSaving(true);

    const formData: ApplicationFormData = {
      organization: selectedOrg,
      specialist: selectedSpecialist,
      subfolderName: subfolderName,
      students: studentsData
    };

    try {
      await onSaveDraft(formData);
      setSubmitError(null);
      setSubmitSuccess(`Черновик успешно сохранен.`);
      setIsStatusVisible(true);
    } catch (error: any) {
      console.error('Draft save error:', error);
      setSubmitSuccess(null);
      setSubmitError(error?.message || `Не удалось сохранить черновик.`);
      setIsStatusVisible(true);
      window.scrollTo(0, 0);
    } finally {
      setIsDraftSaving(false);
    }
  };

  const pageTitle = isEditing ? (onSaveDraft ? 'Редактирование черновика' : 'Редактирование заявки') : 'Создание новой заявки';
  const TitleIcon = isEditing ? EditNoteIcon : NoteAddIcon;

  const isSuccess = Boolean(submitSuccess);
  const isError = Boolean(submitError);

  const toastOpacityClass = isSuccess
    ? isStatusVisible
      ? 'opacity-100'
      : 'opacity-0'
    : 'opacity-100';

  return (
    <div className="w-full">
      {/* Toast Notification */}
      {(isError || isSuccess) && (
        <div
          className={`
            fixed top-20 left-1/2 z-50 -translate-x-1/2
            transition-opacity duration-300 ease-in-out
            ${toastOpacityClass}
          `}
        >
          <div
            className={`
              flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg border
              ${
                isError
                  ? 'bg-red-100 text-red-800 border-red-200'
                  : 'bg-blue-100 text-blue-800 border-blue-200'
              }
            `}
          >
            {isError ? (
              <ErrorOutlineIcon className="text-red-600" />
            ) : (
              <PlaylistAddCheckIcon className="text-blue-600" />
            )}
            <span className="text-sm font-semibold">
              {submitError || submitSuccess}
            </span>
          </div>
        </div>
      )}

      <div className="p-4 lg:p-8 space-y-8">
        {/* Header Section */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
            <TitleIcon fontSize="large" className="text-blue-600" />
            {pageTitle}
          </h1>
          <Link
            to="/applications"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 group"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Назад к списку заявок
          </Link>
        </div>

        {/* Form Managers Section */}
        <div className="space-y-6">
          <OrganizationsManager
            onLoad={handleOrganizationsLoad}
            onSelect={setSelectedOrg}
            initialSelectedId={initialData?.organization?.id}
            onOrganizationCreated={handleOrganizationCreated}
          />
          <SubfolderManager value={subfolderName} onChange={setSubfolderName} />
          <ResponsibleSpecialistsManager
            organizations={organizations}
            organizationId={selectedOrg?.id}
            onSelect={setSelectedSpecialist}
            initialSelectedId={initialData?.specialist?.id}
          />
          <ProgramsManager
            onSelect={setSelectedPrograms}
            onApplyToAll={setProgramAssignments}
            onProgramsLoad={setAvailablePrograms}
            initialSelectedIds={initialCommonProgramIds}
            initialSelectedPrograms={initialCommonPrograms}
            initialProgramDetails={initialCommonProgramDetails}
            hasStudents={studentsData.length > 0}
            studentsVersion={studentsVersion}
          />
          <StudentsManager
            appliedPrograms={programAssignments}
            availablePrograms={availablePrograms}
            onDataChange={handleStudentsDataChange}
            initialStudents={initialData?.students}
          />
        </div>

        {/* Action Button */}
        <div className="flex justify-end gap-4">
          {onSaveDraft && (
            <button
              type="button"
              onClick={handleDraftSave}
              disabled={isSubmitting || isDraftSaving}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition-colors"
            >
              {isDraftSaving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DescriptionIcon fontSize="small" />
              )}
              Сохранить черновик
            </button>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isDraftSaving}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors focus:outline-none ${
              (isSubmitting || isDraftSaving)
                ? 'bg-gray-400 cursor-wait'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditing ? (
              <SaveIcon fontSize="small" />
            ) : (
              <PlaylistAddCheckIcon fontSize="small" />
            )}
            {isSubmitting
              ? 'Сохранение...'
              : isEditing
              ? (onSaveDraft ? 'Опубликовать заявку' : 'Сохранить изменения')
              : 'Создать заявку'}
          </button>
        </div>
      </div>
    </div>
  );
};